import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  Typography,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon, MedicalServices, LocalPharmacy, Business, LocationOn } from '@mui/icons-material';
import { useTheme, useMediaQuery } from '@mui/material';

type PersonSuggestion = { type: 'person'; label: string; npi: string; credential?: string | null; entityType?: number; specialty?: string | null; city?: string | null; state?: string | null };
type SpecialtySuggestion = { type: 'specialty'; value: string };
type LocationSuggestion = { type: 'location'; value: string };
type SuggestionItem = PersonSuggestion | SpecialtySuggestion | LocationSuggestion | { type: 'label'; label: string };

interface SearchBarProps {
  placeholder?: string;
  hero?: boolean;
  autoFocus?: boolean;
}

const RECENT_KEY = 'mdcv_recent_searches';

export default function SearchBar({ placeholder, hero, autoFocus }: SearchBarProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

  // Debounced fetch (250ms)
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setActiveIndex(-1);
      return;
    }
    let ignore = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const [peopleRes, specRes, locRes] = await Promise.all([
          fetch(`${API_URL}/api/search/suggest?q=${encodeURIComponent(query)}&type=name`),
          fetch(`${API_URL}/api/search/suggest?q=${encodeURIComponent(query)}&type=specialty`),
          fetch(`${API_URL}/api/search/suggest?q=${encodeURIComponent(query)}&type=location`),
        ]);
        const [people, specs, locs] = await Promise.all([peopleRes.json(), specRes.json(), locRes.json()]);
        if (ignore) return;
        // Build groups, then cap to 8 selectable rows total
        const peopleItems: SuggestionItem[] = (people?.suggestions || [])
          .slice(0, 5)
          .map((p: any) => ({ type: 'person', label: p.label, npi: String(p.npi), credential: p.credential, entityType: p.entityType, specialty: p.specialty, city: p.city, state: p.state }));
        const specItems: SuggestionItem[] = (specs?.suggestions || [])
          .slice(0, 5)
          .map((s: any) => ({ type: 'specialty', value: String(s) }));
        const locItems: SuggestionItem[] = (locs?.suggestions || [])
          .slice(0, 5)
          .map((l: any) => ({ type: 'location', value: String(l) }));

        const maxSelectable = 8;
        const items: SuggestionItem[] = [];
        let remaining = maxSelectable;
        if (peopleItems.length) {
          items.push({ type: 'label', label: 'People' });
          const take = Math.min(peopleItems.length, remaining);
          items.push(...peopleItems.slice(0, take));
          remaining -= take;
        }
        if (remaining > 0 && specItems.length) {
          items.push({ type: 'label', label: 'Specialties' });
          const take = Math.min(specItems.length, remaining);
          items.push(...specItems.slice(0, take));
          remaining -= take;
        }
        if (remaining > 0 && locItems.length) {
          items.push({ type: 'label', label: 'Locations' });
          const take = Math.min(locItems.length, remaining);
          items.push(...locItems.slice(0, take));
        }
        setSuggestions(items);
        setActiveIndex(items.findIndex(i => i.type !== 'label'));
      } catch {
        setSuggestions([]);
        setActiveIndex(-1);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => { clearTimeout(t); ignore = true; };
  }, [query]);

  // Outside click to close suggestions
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target as Node)) {
        setSuggestOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const handleSubmit = async (q?: string) => {
    const val = (q ?? query).trim();
    if (!val) return;

    // Save recent searches (mobile focus mode)
    try {
      const rec = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      const newRec = [val, ...rec.filter((r: string) => r !== val)].slice(0, 5);
      localStorage.setItem(RECENT_KEY, JSON.stringify(newRec));
    } catch {}

    // Handle pattern: role+username (e.g., md+janedoe)
    const handleMatch = val.toLowerCase().match(/^([a-z]{1,4})\+([a-z0-9-_.]{2,})$/);
    if (handleMatch) {
      navigate(`/${handleMatch[1]}+${handleMatch[2]}`);
      return;
    }
    const plusMatch = val.toLowerCase().match(/^\+([a-z0-9-_.]{2,})$/);
    if (plusMatch) {
      const uname = plusMatch[1];
      try {
        const res = await fetch(`${API_URL}/api/profiles/${encodeURIComponent(uname)}`);
        if (res.ok) {
          const data = await res.json();
          const pref = deriveRolePrefix(data?.credential, data?.entity_type);
          navigate(`/${pref}+${uname}`);
          return;
        }
      } catch {}
      navigate(`/search?q=${encodeURIComponent(val)}`);
      return;
    }
    navigate(`/search?q=${encodeURIComponent(val)}`);
  };

  function deriveRolePrefix(credential?: string, entityType?: number): string {
    const c = (credential || '').toUpperCase();
    if (c.includes('MD')) return 'md';
    if (c.includes('DO')) return 'do';
    if (c.includes('RN')) return 'rn';
    if (c.includes('NP') || c.includes('NURSE PRACTITIONER')) return 'np';
    if (c.includes('PA')) return 'pa';
    if (c.includes('DDS')) return 'dds';
    if (c.includes('DMD')) return 'dmd';
    if (c.includes('PHARMD')) return 'pharmd';
    if (entityType === 2) return 'org';
    return 'md';
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestOpen || suggestions.length === 0) {
      if (e.key === 'Enter') handleSubmit();
      return;
    }
    const focusables = suggestions.map((s, idx) => ({ s, idx })).filter(x => x.s.type !== 'label');
    const current = focusables.findIndex(x => x.idx === activeIndex);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = focusables[(current + 1 + focusables.length) % focusables.length]?.idx ?? -1;
      setActiveIndex(next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = focusables[(current - 1 + focusables.length) % focusables.length]?.idx ?? -1;
      setActiveIndex(next);
    } else if (e.key === 'Enter') {
      const item = suggestions[activeIndex];
      if (item && item.type !== 'label') {
        selectSuggestion(item as any);
      } else {
        handleSubmit();
      }
    } else if (e.key === 'Escape') {
      setSuggestOpen(false);
    }
  };

  const selectSuggestion = (item: PersonSuggestion | SpecialtySuggestion | LocationSuggestion) => {
    if (item.type === 'person') {
      navigate(`/provider/${item.npi}`);
    } else if (item.type === 'specialty') {
      navigate(`/search?specialty=${encodeURIComponent(item.value)}`);
    } else if (item.type === 'location') {
      const [city, state] = item.value.split(',').map((s) => s.trim());
      const params = new URLSearchParams();
      if (city) params.set('city', city);
      if (state) params.set('state', state);
      navigate(`/search?${params.toString()}`);
    }
  };

  const inputHeight = isMobile ? 50 : 60; // within requested ranges

  // Mobile focus mode overlay
  const recent = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch { return []; }
  }, [focused]);

  return (
    <Box ref={boxRef} sx={{ position: 'relative' }}>
      <Paper
        component="form"
        onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleSubmit(); }}
        sx={{
          p: 0,
          display: 'flex',
          alignItems: 'center',
          mx: 'auto',
          borderRadius: 2,
          overflow: 'hidden',
          width: '100%',
          // Widen hero search bar on desktop viewports
          maxWidth: hero ? { xs: '100%', sm: 720, md: 1080, lg: 1200 } : '100%',
        }}
        elevation={hero ? 3 : 1}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          variant="standard"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { setFocused(true); setSuggestOpen(true); }}
          onBlur={() => { if (isMobile) {/* keep focus mode */} else { /* leave suggestions handled via outside click */ } }}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 2 }}>
                <SearchIcon />
              </InputAdornment>
            ),
            sx: { fontSize: { xs: '1rem', md: '1.1rem' }, height: inputHeight, alignItems: 'center' },
          }}
        />
      </Paper>

      {/* Desktop dropdown suggestions */}
      {!isMobile && suggestOpen && suggestions.length > 0 && (
        <Paper
          sx={{ position: 'absolute', top: '100%', left: 0, right: 0, mt: 1, p: 1, maxHeight: 360, overflowY: 'auto', zIndex: 10 }}
        >
          {suggestions.map((s, idx) => (
            s.type === 'label' ? (
              <Typography key={`label-${idx}`} variant="caption" sx={{ px: 1.5, pt: 1, pb: 0.5, display: 'block', color: 'text.secondary' }}>
                {s.label}
              </Typography>
            ) : (
              <Box
                key={`s-${idx}`}
                onMouseDown={(e) => { e.preventDefault(); }}
                onClick={() => selectSuggestion(s as any)}
                sx={{
                  px: 1.5, py: 1,
                  cursor: 'pointer', borderRadius: 1,
                  bgcolor: idx === activeIndex ? 'action.hover' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                  display: 'flex', alignItems: 'center', gap: 1,
                }}
              >
                {s.type === 'person' && (() => {
                  const p = s as PersonSuggestion;
                  const pref = (p.credential || '').toLowerCase();
                  const isOrg = p.entityType === 2;
                  const Icon = isOrg ? Business : pref.includes('pharm') ? LocalPharmacy : MedicalServices;
                  const abbr = isOrg ? 'ORG' : pref.includes('md') || pref.includes('m.d') || pref.includes('mbbs') || pref.includes('mbchb') ? 'MD'
                    : pref.includes('do') ? 'DO'
                    : pref.includes('dds') ? 'DDS'
                    : pref.includes('dmd') ? 'DMD'
                    : pref.includes('np') || pref.includes('aprn') ? 'NP'
                    : pref.includes('pa') ? 'PA'
                    : pref.includes('rn') ? 'RN'
                    : pref.includes('pharm') ? 'PHARMD'
                    : 'PRO';
                  const chipStyle = (abbr: string) => {
                    const map: Record<string, string> = {
                      MD: '#0D6EFD',
                      DO: '#6F42C1',
                      DDS: '#0B7285',
                      DMD: '#0B7285',
                      NP: '#198754',
                      PA: '#B35C00',
                      RN: '#0C8599',
                      PHARMD: '#0E6B50',
                      ORG: '#374151'
                    };
                    const bg = map[abbr] || '#0D6EFD';
                    return { bgcolor: bg, color: '#fff' } as const;
                  };
                  const chipSx = chipStyle(abbr);
                  return (
                    <>
                      <Icon sx={{ color: chipSx.bgcolor }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }}>{p.label} <Box component="span" sx={{ ml: 1, px: 0.75, py: 0.25, bgcolor: chipSx.bgcolor, color: chipSx.color, borderRadius: 1, fontSize: 12 }}>{abbr}</Box></Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          {(p.specialty || '').trim()}{(p.city || p.state) ? ` • ${p.city || ''}${p.city && p.state ? ', ' : ''}${p.state || ''}` : ''}
                        </Typography>
                      </Box>
                    </>
                  );
                })()}
                {s.type === 'specialty' && (
                  <>
                    <MedicalServices sx={{ color: 'text.secondary' }} />
                    <Typography color="text.secondary">{(s as SpecialtySuggestion).value}</Typography>
                  </>
                )}
                {s.type === 'location' && (
                  <>
                    <LocationOn sx={{ color: 'text.secondary' }} />
                    <Typography color="text.secondary">{(s as LocationSuggestion).value}</Typography>
                  </>
                )}
              </Box>
            )
          ))}
        </Paper>
      )}

      {/* Mobile full-screen focus mode */}
      {isMobile && focused && (
        <Box
          sx={{
            position: 'fixed', inset: 0, zIndex: 1200, bgcolor: 'background.default',
            pt: 'env(safe-area-inset-top)',
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              autoFocus
              placeholder={placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { handleSubmit(); } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: { fontSize: '1.1rem', height: 56 },
              }}
            />
            <Button onClick={() => { setFocused(false); setSuggestOpen(false); }} startIcon={<CloseIcon />}>Cancel</Button>
          </Box>

          {recent?.length > 0 && (
            <Box sx={{ px: 2, pb: 1 }}>
              <Typography variant="caption" color="text.secondary">Recent</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, overflowX: 'auto' }}>
                {recent.map((r: string) => (
                  <Chip key={r} label={r} onClick={() => handleSubmit(r)} sx={{ borderRadius: 2 }} />
                ))}
              </Box>
            </Box>
          )}

          <Divider />

          <Box sx={{ p: 1 }}>
            {loading && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Loading…</Typography>
            )}
            {!loading && suggestions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Type to search</Typography>
            )}
            {suggestions.map((s, idx) => (
              s.type === 'label' ? (
                <Typography key={`ml-${idx}`} variant="overline" sx={{ px: 2, pt: 1, display: 'block' }}>{s.label}</Typography>
              ) : (
                <Box key={`mi-${idx}`} onClick={() => selectSuggestion(s as any)} sx={{ px: 2, py: 1.25, cursor: 'pointer', '&:active': { opacity: 0.8 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {s.type === 'person' && (() => {
                    const p = s as PersonSuggestion;
                    const pref = (p.credential || '').toLowerCase();
                    const isOrg = p.entityType === 2;
                    const Icon = isOrg ? Business : pref.includes('pharm') ? LocalPharmacy : MedicalServices;
                    const abbr = isOrg ? 'ORG' : pref.includes('md') || pref.includes('m.d') || pref.includes('mbbs') || pref.includes('mbchb') ? 'MD'
                      : pref.includes('do') ? 'DO'
                      : pref.includes('dds') ? 'DDS'
                      : pref.includes('dmd') ? 'DMD'
                      : pref.includes('np') || pref.includes('aprn') ? 'NP'
                      : pref.includes('pa') ? 'PA'
                      : pref.includes('rn') ? 'RN'
                      : pref.includes('pharm') ? 'PHARMD'
                      : 'PRO';
                    const chipStyle = (abbr: string) => {
                      const map: Record<string, string> = {
                        MD: '#0D6EFD',
                        DO: '#6F42C1',
                        DDS: '#0B7285',
                        DMD: '#0B7285',
                        NP: '#198754',
                        PA: '#B35C00',
                        RN: '#0C8599',
                        PHARMD: '#0E6B50',
                        ORG: '#374151'
                      };
                      const bg = map[abbr] || '#0D6EFD';
                      return { bgcolor: bg, color: '#fff' } as const;
                    };
                    const chipSx = chipStyle(abbr);
                    return (
                      <>
                        <Icon sx={{ color: chipSx.bgcolor }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }}>{p.label} <Box component="span" sx={{ ml: 1, px: 0.75, py: 0.25, bgcolor: chipSx.bgcolor, color: chipSx.color, borderRadius: 1, fontSize: 12 }}>{abbr}</Box></Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                            {(p.specialty || '').trim()}{(p.city || p.state) ? ` • ${p.city || ''}${p.city && p.state ? ', ' : ''}${p.state || ''}` : ''}
                          </Typography>
                        </Box>
                      </>
                    );
                  })()}
                  {s.type === 'specialty' && (
                    <>
                      <MedicalServices sx={{ color: 'text.secondary' }} />
                      <Typography color="text.secondary">{(s as SpecialtySuggestion).value}</Typography>
                    </>
                  )}
                  {s.type === 'location' && (
                    <>
                      <LocationOn sx={{ color: 'text.secondary' }} />
                      <Typography color="text.secondary">{(s as LocationSuggestion).value}</Typography>
                    </>
                  )}
                </Box>
              )
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
